import moment from "moment";

/**
 * Turn an ISO-8601 timestamp into a "2 minutes ago"-style string.
 * Matches the server's Carbon diffForHumans() output so cached and
 * client-computed strings look identical. Returns null if iso is missing/invalid.
 */
const relativeFromIso = (iso) => {
    if (!iso) return null;
    const m = moment(iso);
    return m.isValid() ? m.fromNow() : null;
};

const getNextSibling = function (elem, selector) {
    let sibling = elem.nextElementSibling;
    if (!selector) return sibling;
    while (sibling) {
        if (sibling.matches(selector)) return sibling;
        sibling = sibling.nextElementSibling;
    }
};

const getPreviousSibling = function (elem, selector) {
    var sibling = elem.previousElementSibling;
    if (!selector) return sibling;
    while (sibling) {
        if (sibling.matches(selector)) return sibling;
        sibling = sibling.previousElementSibling;
    }
};

const debounce = (fn, delay) => {
    var timer = null;
    return function () {
      var context = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
}

function throttle(mainFunction, delay) {
  let timerFlag = null;

  return (...args) => {
    if (timerFlag === null) { 
      mainFunction(...args);
      timerFlag = setTimeout(() => {
        timerFlag = null; 
      }, delay);
    }
  };
}

function trapFocus(node) {
    const focusableElements = node.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    const trap = (e) => {
        let isTabPressed = e.key === 'Tab' || e.keyCode === 9;

        if (!isTabPressed) {
            return;
        }

        if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus();
                e.preventDefault();
            }
        } else {
            if (document.activeElement === lastFocusableElement) {
                firstFocusableElement.focus();
                e.preventDefault();
            }
        }
    };

    node.addEventListener('keydown', trap);

    // Return a function to remove the event listener and stop trapping focus
    return () => {
        node.removeEventListener('keydown', trap);
    };
}

// Given a value, cast it to a Boolean
const castToBoolean = (val) => {
    let type = typeof val
  
    switch (type) {
      case 'string': {
        let num = parseInt(val, 10)
        if (isNaN(num)) {
          return val === 'true' // 'true' or 'false'
        } else {
          return Boolean(num) // '1' or '0'
        }
      }
      case 'number':
        // Probably 1 or 0
        return Boolean(val)
      case 'boolean':
        // Already a boolean
        return val
      default:
        // Old JS hack way, works most of the time :-)
        return !!val
    }
  }

function getCustomFormula(innerHTML) {
  // Create a container to hold the HTML content
  let container = document.createElement('div');
  container.innerHTML = innerHTML;

  // Function to traverse and extract the formula
  function traverse(node) {
    let formula = '';
      node.childNodes.forEach(child => {
        if(!child) return; // Skip if child is null or undefined
        if(child.classList && child.classList.contains('ql-hidden')) return; // Skip if it's a mention

          if (child.nodeType === Node.TEXT_NODE) {
              // Append text content directly
              formula += " " + child.textContent.trim() + " ";
          } else if (child.nodeType === Node.ELEMENT_NODE && child.classList.contains('mention')) {
            if (child.innerHTML.trim() === '') {
              return;
            };
            
              // Replace span content with its data-value attribute
              let dataValue = child.getAttribute('data-value');
              if (dataValue) {
                  formula += dataValue;
              } else {
                  formula += traverse(child); // Recurse for nested spans
              }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
              // Traverse other element nodes
              formula += traverse(child);
          }
      });

      return formula;
  }
  
  return traverse(container).trim();
}

/**
 * A metric's `goal_criteria` encodes which way is "good": `less_than_or_equal` means lower is
 * better, and everything else — including a null/unset direction — means higher is better, matching
 * the app's default goal direction. Centralised so the scorecard's delta surfaces (the metric drawer
 * header and the grid delta pills / improving-declining filter) agree on the rule and never drift
 * apart on the magic string.
 *
 * @param {string|null|undefined} goalCriteria
 * @returns {boolean}
 */
function metricHigherIsBetter(goalCriteria) {
    return goalCriteria !== 'less_than_or_equal';
}

/**
 * A scorecard delta's tone — whether the change was good for the metric, independent of which way it
 * moved. Produced by the grid's delta mixin and consumed by the tab controls' improving/declining
 * filter, so it is a cross-module contract: renaming a value on one side would silently make the
 * filter match nothing.
 */
const DELTA_TONE = { GOOD: 'good', BAD: 'bad', NEUTRAL: 'neutral' };

/**
 * Whether a click originated on the `<label for="{id}">` that labels this control.
 *
 * The select2 trigger carries the caller's raw id, so the browser forwards a click on that label
 * as a synthetic click on the trigger — which toggles the menu itself. The label sits outside the
 * element holding Alpine's `@click.away`, so without this guard the away handler closes the menu
 * first and the forwarded click immediately reopens it: the label could open the dropdown but
 * never close it. Both select2 variants share the behaviour, hence a shared helper.
 *
 * @param {Event} event
 * @param {string|null|undefined} id  the control's raw id, i.e. what a label's `for` points at
 * @returns {boolean}
 */
function isEventFromLabelFor(event, id) {
    if (!id || typeof event?.target?.closest !== 'function') return false;

    // `htmlFor` reflects the `for` attribute, so this matches only a label bound to THIS control —
    // a label wrapping some other control, or none, leaves the away behaviour untouched.
    return event.target.closest('label')?.htmlFor === id;
}

/**
 * Whether an `@focusout` / `@click.away` event should close an open select2 menu.
 *
 * Both select2 variants bind the same handler to the element wrapping their trigger, and this is
 * the subtlest logic in that pair: the mousedown-before-click race below only came to light by
 * driving it in a real browser. Held here as one implementation because a copy per variant drifts
 * the moment someone closes the residual noted below — the same two-file drift that made the raw
 * `id` wiring a bug in the first place.
 *
 * Scope — the select family is FOUR wide, and only the two select2 variants route through this.
 * select-multi.blade.php and select-multi-server.blade.php still render only the derived
 * `label-{id}`, never the raw `id`, and still carry their own hand-rolled focus-out handlers. That
 * is deliberate: no caller labels either of them today, and giving them the raw `id` WITHOUT also
 * routing their handler through this function would reintroduce the close-then-reopen race — the
 * label click closes the menu via @click.away, and the browser's forwarded click reopens it. If you
 * ever put a <label for> on a select-multi, do both halves together.
 *
 * @param {Event} event
 * @param {{triggerId: string|null|undefined, componentEl: Element, menuEl: Element}} refs
 *        `triggerId` is read off the trigger element rather than an x-data snapshot: a keyless
 *        Livewire morph can rewrite the button's id without re-running x-data, and a stale id
 *        silently reverts the control to the close-then-reopen bug.
 * @returns {boolean} true when the caller should close the menu
 */
function shouldCloseOnFocusOut(event, { triggerId, componentEl, menuEl }) {
    // Let the trigger's own click handler toggle: the browser forwards this label click to it.
    if (isEventFromLabelFor(event, triggerId)) return false;

    // A focusout carrying no relatedTarget is the blur a mousedown fires BEFORE its click, so focus
    // has not landed anywhere yet and we cannot tell an outside click from a press on our own label
    // (which blurs the popup's search input, not the label). Closing here loses that race: the
    // forwarded label click would then reopen the menu.
    if (event.type === 'focusout' && event.relatedTarget === null) {
        // Only defer while focus is still inside this document — that is what makes the blur an
        // in-page press with a click on the way, so @click.away will run and close for us. Once
        // focus has left the document entirely (window / tab blur), no document click ever follows
        // and deferring would strand the menu open, so close here as these components did before
        // the label guards existed. Right-click and iOS taps on non-interactive targets keep focus
        // in the document and so are still deferred — a known, recoverable residual.
        return !document.hasFocus();
    }

    return !componentEl.contains(event.relatedTarget) && !menuEl.contains(event.relatedTarget);
}

export {
    getNextSibling,
    getPreviousSibling,
    debounce,
    throttle,
    trapFocus,
    castToBoolean,
    getCustomFormula,
    relativeFromIso,
    metricHigherIsBetter,
    shouldCloseOnFocusOut,
    DELTA_TONE
};
